import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { servicesContent } from '../data/servicesContent';

const scrollToContact = (service) => {
  if (service) window.dispatchEvent(new CustomEvent('soulboard:apply', { detail: { service } }));
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
};

/* Reuses THE SOULBOARD METHOD accordion visual logic exactly: bold title with a
   small uppercase purple descriptor visible in the closed state, neutral chevron,
   horizontal rules, one row open at a time (first open by default). */
const ProcessAccordion = ({ steps, testid, dark = false }) => {
  const [open, setOpen] = useState(0);
  const border = dark ? 'border-[#2a3a30]' : 'border-neutral-300';
  const titleColor = dark ? 'text-[#faf7ee]' : 'text-neutral-900';
  const bodyColor = dark ? 'text-[#cbd5cc]' : 'text-neutral-700';
  const descColor = dark ? '#a996d9' : '#7d65b8';
  const chevColor = dark ? 'text-[#8a9a90]' : 'text-neutral-500';
  return (
    <div className={`border-t ${border}`} data-testid={testid}>
      {steps.map((st, i) => (
        <div key={st.title} className={`border-b ${border}`} data-testid={`${testid}-row-${i}`}>
          <button
            type="button"
            onClick={() => setOpen(open === i ? -1 : i)}
            className="w-full flex items-center justify-between gap-3 py-4 text-left"
            data-testid={`${testid}-toggle-${i}`}
          >
            <span className="flex-1">
              <span className={`block text-base lg:text-lg font-bold leading-tight ${titleColor}`}>{st.title}</span>
              {st.descriptor && (
                <span className="block text-[10px] tracking-[0.25em] uppercase mt-1" style={{ color: descColor }}>
                  {st.descriptor}
                </span>
              )}
            </span>
            <ChevronDown
              size={18}
              className={`flex-shrink-0 ${chevColor} transition-transform duration-300 ${open === i ? 'rotate-180' : ''}`}
            />
          </button>
          <AnimatePresence initial={false}>
            {open === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
                className="overflow-hidden"
              >
                <div className="pb-5 pl-10 max-w-3xl">
                  <p className={`text-sm lg:text-base leading-relaxed ${bodyColor}`}>{st.body}</p>
                  {st.list && (
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 mt-4">
                      {st.list.map((li) => (
                        <li key={li} className={`text-sm flex items-start gap-2 ${dark ? 'text-[#e7e3d8]' : 'text-neutral-800'}`}>
                          <span className="mt-0.5 font-bold" style={{ color: '#CCFF00' }}>·</span>
                          {li}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ))}
    </div>
  );
};

/* LAYER 1 editorial intro + LAYER 2 expandable process. Alternating image rhythm. */
const ServiceSection = ({ s, reverse }) => (
  <section id={s.anchor} className="py-16 lg:py-24 scroll-mt-24" data-testid={`service-${s.anchor}`}>
    <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
        <motion.div
          className={reverse ? 'lg:order-2' : 'lg:order-1'}
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <img src={s.image} alt={s.title} className="block w-full h-auto" loading="lazy" />
        </motion.div>

        <motion.div
          className={`space-y-5 ${reverse ? 'lg:order-1' : 'lg:order-2'}`}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <span className="block h-px w-12 bg-neutral-900" />
          <h3
            className="offering-title leading-[0.95] text-neutral-900"
            style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2rem, 4vw, 3.25rem)' }}
          >
            {s.title}
          </h3>
          <p className="text-base lg:text-lg italic text-neutral-700">{s.tagline}</p>
          {s.meta && (
            <div className="flex flex-wrap gap-x-8 gap-y-2">
              {s.meta.map((m) => (
                <div key={m.label}>
                  <p className="text-[10px] tracking-[0.3em] uppercase" style={{ color: '#7d65b8' }}>{m.label}</p>
                  <p className="text-sm lg:text-base text-neutral-900 font-medium">{m.value}</p>
                </div>
              ))}
            </div>
          )}
          <p className="text-3xl lg:text-4xl text-neutral-900" style={{ fontWeight: 500 }} data-testid={`service-${s.anchor}-price`}>
            {s.price}
          </p>
          {s.body.map((p, i) => (
            <p key={i} className="text-sm lg:text-base text-neutral-700 leading-relaxed">{p}</p>
          ))}
          <div className="pt-1">
            <motion.button
              onClick={() => scrollToContact(s.anchor)}
              className="px-7 py-3.5 text-sm uppercase tracking-[0.25em] font-bold"
              style={{ background: '#CCFF00', color: '#171717' }}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              data-testid={`service-${s.anchor}-cta`}
            >
              [ {s.cta} ]
            </motion.button>
          </div>
        </motion.div>
      </div>

      <div className="mt-14 lg:mt-16">
        <p className="text-[11px] tracking-[0.4em] uppercase text-neutral-500 mb-4">{s.processLabel}</p>
        <ProcessAccordion steps={s.process} testid={`process-${s.anchor}`} />
      </div>
    </div>
  </section>
);

/* Signature culmination of the Hands On chapter — elevated hero + expandable details. */
const StyleShift = ({ s }) => {
  const steps = s.process.map((p, i) => (i === s.process.length - 1 ? { ...p, list: s.blueprint } : p));
  return (
    <section id={s.anchor} className="py-20 lg:py-28 bg-[#0e1a14] text-[#faf7ee] scroll-mt-24" data-testid={`service-${s.anchor}`}>
      <div className="container mx-auto px-6 lg:px-8 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <img src={s.image} alt={s.title} className="block w-full h-auto" loading="lazy" />
          </motion.div>

          <motion.div
            className="lg:col-span-5 space-y-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block text-[10px] tracking-[0.3em] font-bold px-3 py-1" style={{ background: '#CCFF00', color: '#171717' }}>
              {s.label}
            </span>
            <h3 className="offering-title leading-[0.95]" style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.5rem, 5.5vw, 4.5rem)' }}>
              {s.title}
            </h3>
            <p className="text-lg lg:text-xl italic" style={{ color: '#cbd5cc' }}>{s.tagline}</p>
            {s.format && (
              <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: '#CCFF00' }}>{s.format}</p>
            )}
            <div>
              {s.priceLabel && <p className="text-[10px] tracking-[0.35em] uppercase" style={{ color: '#CCFF00' }}>{s.priceLabel}</p>}
              <p className="text-3xl lg:text-4xl" style={{ fontWeight: 500 }} data-testid={`service-${s.anchor}-price`}>{s.price}</p>
              <p className="text-sm italic mt-1" style={{ color: '#cbd5cc' }}>{s.availability}</p>
            </div>
            {s.body.map((p, i) => (
              <p key={i} className="text-sm lg:text-base leading-relaxed" style={{ color: '#e7e3d8' }}>{p}</p>
            ))}
            <div className="pt-1">
              <motion.button
                onClick={() => scrollToContact(s.anchor)}
                className="px-8 py-4 text-sm uppercase tracking-[0.2em] font-bold"
                style={{ background: '#CCFF00', color: '#171717' }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                data-testid={`service-${s.anchor}-cta`}
              >
                [ {s.cta} ]
              </motion.button>
            </div>
          </motion.div>
        </div>

        <div className="mt-14 lg:mt-16">
          <p className="text-[11px] tracking-[0.4em] uppercase mb-4" style={{ color: '#8a9a90' }}>{s.processLabel}</p>
          <ProcessAccordion steps={steps} testid={`process-${s.anchor}`} dark />
        </div>
      </div>
    </section>
  );
};

const HandsOnStyling = () => {
  const { language } = useLanguage();
  const c = servicesContent[language];
  const sv = c.services;

  return (
    <div className="bg-[#faf7ee]" data-testid="hands-on-styling">
      {/* Chapter opening — mirrors DISCOVER & DEVELOP YOUR STYLE */}
      <section id="hands-on" className="pt-20 lg:pt-28 pb-6 lg:pb-8 overflow-hidden" data-testid="hands-on-chapter">
        <div className="container mx-auto px-6 lg:px-8">
          <motion.div
            className="max-w-5xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-12 bg-neutral-900" />
              <span className="text-xs tracking-[0.4em] font-bold uppercase text-neutral-900">{c.hoPathLabel}</span>
            </div>
            <h2
              className="offering-title leading-[0.92] text-neutral-900"
              style={{ fontWeight: 400, letterSpacing: '0.005em', fontSize: 'clamp(2.5rem, 7vw, 5rem)' }}
            >
              {c.hoTitleLine1}
              <br />
              {c.hoTitleLine2}
            </h2>
            <div className="w-14 h-[3px] bg-[#CCFF00] mt-6 mb-6" />
            <p className="text-base lg:text-xl text-neutral-700 leading-relaxed max-w-3xl">{c.hoChapterCopy}</p>
            <div className="space-y-3 mt-5 max-w-3xl">
              {c.hoIntro.map((p, i) => (
                <p key={i} className="text-sm lg:text-base text-neutral-600 leading-relaxed">{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <ServiceSection s={sv.wardrobeEdit} reverse={false} />
      <ServiceSection s={sv.personalShopping} reverse={true} />
      <ServiceSection s={sv.personalSourcing} reverse={false} />
      <StyleShift s={sv.wardrobeReset} />
    </div>
  );
};

export default HandsOnStyling;
